import { rem } from "@certn/cascadia";
import {
  CascadiaMiniIcon,
  lineHeights,
  sizes,
  textStyles,
  weights,
} from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import React, { MouseEventHandler, RefObject, useCallback } from "react";
import { AriaTextFieldProps, useTextField } from "react-aria";

import { AccessibilityWarnings } from "../../../components/inputs/AccessibilityWarnings";
import { FieldErrorsProps } from "../../../components/inputs/_assets/input-wrapper/FieldErrors";
import { FormFeedback } from "../../../components/inputs/_assets/input-wrapper/FormFeedback";
import { FormLabel } from "../../../components/inputs/_assets/input-wrapper/FormLabel";
import { FormText } from "../../../components/inputs/_assets/input-wrapper/FormText";
import { InputWrapperProps } from "../../../components/inputs/_assets/input-wrapper/InputWrapper";
import { Icon } from "../../../foundations/icons/Icon/Icon";
import { useColors } from "../../../hooks/useColors";

export interface TextFieldProps extends AriaTextFieldProps, InputWrapperProps {
  info?: React.ReactNode;
  icon?: CascadiaMiniIcon;
  clearable?: boolean;
  successMessage?: string;
  inputRef?: RefObject<HTMLInputElement>;
  hasIconRight?: boolean;
  onIconClick?: MouseEventHandler<HTMLButtonElement> | undefined;
  testID?: string;
  errors?: FieldErrorsProps["children"];
  optionalText?: string;
}

/**
 * @deprecated see TextFieldRAC
 */
export function TextField(props: TextFieldProps) {
  const {
    label,
    validationState,
    placeholder,
    testID = "text-input",
    onIconClick,
    isDisabled,
    clearable,
    hasIconRight,
    icon,
  } = props;
  const colors = useColors();
  const error = validationState === "invalid";
  const values = textStyles["input/text-field"];
  const ref = React.useRef<HTMLInputElement>(null);
  const { labelProps, inputProps, descriptionProps, errorMessageProps } =
    useTextField(props, ref);

  const handleClear = useCallback(() => {
    props.onChange && props.onChange("");
  }, [props]);

  return (
    <>
      <FormLabel
        {...labelProps}
        isRequired={props.isRequired}
        optionalText={props.optionalText}
        info={props.info}
      >
        {label}
      </FormLabel>
      <div className={css(styles.relativeBlock)}>
        {props.icon && !props.hasIconRight && (
          <div className={css(styles.leftIconStyles)}>
            <Icon
              name={props.icon}
              color="icon-base"
              type="mini"
              size="space-5"
            />
          </div>
        )}
        <input
          {...inputProps}
          placeholder={validationState === "invalid" ? "" : placeholder}
          className={css(styles.input, {
            "&:invalid": {
              border: `1px solid ${colors["border-danger-input"]}`,
            },
            "::placeholder": {
              color: "var(--text-placeholder)",
            },
            "&:hover:not(:focus)": props.isDisabled
              ? { borderColor: undefined }
              : !error
                ? {
                    borderColor: "var(--border-input-active)",
                  }
                : null,
            "&:focus": error
              ? css([
                  {
                    outline: "0",
                    borderColor: "var(--border-danger-input)",
                    background: "var(--background-base)",
                    boxShadow: "var(--focus-input-error)",
                  },
                ])
              : css([
                  {
                    outline: "0",
                    borderColor: "var(--border-input-active)",
                    boxShadow: "var(--focus-input)",
                  },
                ]),
            paddingLeft:
              icon && !hasIconRight ? "var(--space-9)" : "var(--space-3)",
            border: props.isDisabled
              ? `1px solid ${colors["border-input-disabled"]}`
              : "none",
            color: props.isDisabled
              ? "var(--text-disabled)"
              : "var(--text-base)",
            backgroundColor: props.isDisabled
              ? "var(--background-input-disabled)"
              : "var(--background-input)",
            paddingRight:
              clearable || hasIconRight ? "var(--space-9)" : "var(--space-3)",
            boxShadow: error
              ? `inset 0 0 0 1px ${colors["border-danger-input"]}`
              : isDisabled
                ? `inset 0 0 0 1px ${colors["background-input-disabled"]}`
                : `inset 0 0 0 1px ${colors["border-strong"]}`,
            fontSize: rem(sizes[values.size]),
            lineHeight: rem(lineHeights[values.lineHeight]),
            fontWeight: weights[values.weight],
          })}
          ref={ref}
          data-testid={testID}
        />
        {props.icon && props.hasIconRight && (
          <button
            className={css(styles.rightIconStyles, {
              color: "var(--icon-base)",
            })}
            onClick={onIconClick}
          >
            <Icon
              name={props.icon}
              color="icon-base"
              type="mini"
              size="space-5"
            />
          </button>
        )}
        {props.clearable && (
          <button className={css(styles.rightIconStyles)} onClick={handleClear}>
            <Icon name="cancel" color="icon-base" type="mini" size="space-5" />
          </button>
        )}
      </div>
      <FormText {...descriptionProps}>{props.description}</FormText>
      <FormFeedback {...errorMessageProps}>{props.errors}</FormFeedback>
      <FormFeedback type="valid">{props.successMessage}</FormFeedback>
      <AccessibilityWarnings labelProps={props} />
    </>
  );
}

const styles = {
  input: css({
    WebkitAppearance: "none",
    width: "100%",
    boxSizing: "border-box",
    paddingTop: "var(--space-2)",
    paddingBottom: "var(--space-2)",
    borderRadius: "var(--border-radius-2)",
    fontFamily: "var(--font-family-default)",
  }),
  leftIconStyles: css({
    position: "absolute",
    left: "var(--space-3)",
    top: "var(--space-2-5)",
  }),
  relativeBlock: css({
    position: "relative",
    flexGrow: 1,
  }),
  rightIconStyles: css({
    position: "absolute",
    right: "var(--space-3)",
    top: "var(--space-2-5)",
    background: "none",
    border: "none",
    padding: 0,
    "&:hover": {
      cursor: "pointer",
    },
  }),
};
